/**
 * Generic K8s Manifest Wrapper
 *
 * Provides a consistent interface for K8s manifest objects with:
 * - Serialization (JSON/YAML)
 * - Validation using tilt-k8s-schemas validators
 * - Fluent transformations via patch method
 * - Cloning capabilities
 */

import { readFileSync } from 'node:fs';
import * as yaml from 'js-yaml';

/**
 * Validation result structure from tilt-k8s-schemas (typia format)
 */
export interface ValidationResult {
  success: boolean;
  errors?: string[] | undefined;
}

/**
 * Typia validation result (actual format from tilt-k8s-schemas)
 */
export interface IValidation<T> {
  success: boolean;
  errors?: Array<{ path: string; expected: string; value: any }>;
  data?: T | undefined;
}

/**
 * Validator function type from tilt-k8s-schemas (typia format)
 */
export type ValidatorFunction<T> = (value: any) => IValidation<T>;

/**
 * Converts typia validation result to our simplified format
 */
function normalizeValidationResult<T>(result: IValidation<T>): ValidationResult {
  return {
    success: result.success,
    errors: result.errors?.map(e => `${e.path}: ${e.expected} (got ${typeof e.value})`) || undefined
  };
}

/**
 * Generic wrapper interface for K8s manifests
 */
export interface K8sManifestWrapper<T> {
  /** The wrapped manifest data */
  readonly data: T;

  /** Serialize to formatted JSON string (validates by default) */
  toJson(options?: { validate?: boolean }): string;

  /** Serialize to YAML string (validates by default) */
  toYaml(options?: { validate?: boolean }): string;

  /** Validate the manifest data */
  validate(): ValidationResult;

  /** Assert validation passes (throws on failure) */
  assert(): void;

  /** Create a deep clone of this wrapper */
  clone(): K8sManifestWrapper<T>;

  /** Patch the data with direct mutations (returns new wrapper) */
  patch(fn: (obj: T) => void): K8sManifestWrapper<T>;
}

/**
 * Creates a K8s manifest wrapper with all utility methods
 * 
 * @param data - The manifest data to wrap
 * @param validator - Validation function from tilt-k8s-schemas
 * @returns Enriched wrapper with serialization and validation methods
 */
export function createManifestWrapper<T>(
  data: T, 
  validator: ValidatorFunction<T>
): K8sManifestWrapper<T> {
  return {
    data,

    toJson(options?: { validate?: boolean }): string {
      const shouldValidate = options?.validate ?? true;
      if (shouldValidate) {
        const result = validator(data);
        if (!result.success) {
          const errors = result.errors?.map(e =>
            `${e.path}: ${e.expected} (got ${typeof e.value})`
          ).join(', ');
          throw new Error(`Validation failed before serialization: ${errors}`);
        }
      }
      return JSON.stringify(data, null, 2);
    },

    toYaml(options?: { validate?: boolean }): string {
      const shouldValidate = options?.validate ?? true;
      if (shouldValidate) {
        const result = validator(data);
        if (!result.success) {
          const errors = result.errors?.map(e =>
            `${e.path}: ${e.expected} (got ${typeof e.value})`
          ).join(', ');
          throw new Error(`Validation failed before serialization: ${errors}`);
        }
      }
      return yaml.dump(data, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false
      });
    },
    
    validate(): ValidationResult {
      const result = validator(data);
      return normalizeValidationResult(result);
    },
    
    assert(): void {
      const result = validator(data);
      if (!result.success) {
        throw result.errors;
      }
    },
    
    clone(): K8sManifestWrapper<T> {
      const clonedData = JSON.parse(JSON.stringify(data)) as T;
      return createManifestWrapper(clonedData, validator);
    },

    patch(fn: (obj: T) => void): K8sManifestWrapper<T> {
      const cloned = JSON.parse(JSON.stringify(data)) as T;
      fn(cloned);
      return createManifestWrapper(cloned, validator);
    }
  };
}

/**
 * Creates a manifest wrapper from a file (YAML or JSON)
 * 
 * @param path - Path to the manifest file
 * @param validator - Validation function
 * @returns Validated wrapper
 * @throws Error if file doesn't exist or validation fails
 */
export function createWrapperFromFile<T>(
  path: string,
  validator: ValidatorFunction<T>
): K8sManifestWrapper<T> {
  const content = readFileSync(path, 'utf8');
  return createWrapperFromString(content, validator);
}

/**
 * Creates a manifest wrapper from a string (YAML or JSON)
 * 
 * @param content - YAML or JSON string content
 * @param validator - Validation function
 * @returns Validated wrapper
 * @throws Error if parsing or validation fails
 */
export function createWrapperFromString<T>(
  content: string,
  validator: ValidatorFunction<T>
): K8sManifestWrapper<T> {
  let data: T;
  
  try {
    // Try JSON first (faster), then YAML
    data = content.trim().startsWith('{') || content.trim().startsWith('[')
      ? JSON.parse(content)
      : yaml.load(content) as T;
  } catch (error) {
    throw new Error(`Failed to parse manifest content: ${error}`);
  }
  
  const wrapper = createManifestWrapper(data, validator);
  wrapper.assert(); // Validate on load
  return wrapper;
}